/**
 * Product Learning Configuration Editor
 *
 * Product 레벨 시작/종료 활동 설정 UI
 * 비디오 유형 선택 시 동영상 자료실에서 동영상을 선택할 수 있도록 연동
 */
import { useState, useEffect } from 'react'
import { Play, CheckCircle, Video, FileQuestion, ChevronDown, ChevronUp, Film, Loader2 } from 'lucide-react'
import type { ProductLearningConfig, ScopedActivityConfig, ScopedQuizOptions } from '@/types/configuration'
import { sharedMediaApi, type SharedMediaResponse } from '@/api/admin'

interface ProductLearningConfigEditorProps {
  config: ProductLearningConfig | undefined
  onChange: (config: ProductLearningConfig) => void
  overridable?: boolean
  onOverridableChange?: (overridable: boolean) => void
}

const defaultQuizOptions: ScopedQuizOptions = {
  questionCount: 20,
  shuffleQuestions: true,
  passingScore: undefined,
  timeLimitMinutes: undefined,
  questionSource: 'incorrect',
}

const defaultActivityConfig: ScopedActivityConfig = {
  enabled: false,
  types: ['quiz'],
  quizOptions: defaultQuizOptions,
}

export function ProductLearningConfigEditor({ config, onChange, overridable, onOverridableChange }: ProductLearningConfigEditorProps) {
  const [expandedSection, setExpandedSection] = useState<'start' | 'end' | null>('start')
  const [videoList, setVideoList] = useState<SharedMediaResponse[]>([])
  const [videoListLoading, setVideoListLoading] = useState(false)
  const [videoListLoaded, setVideoListLoaded] = useState(false)

  const startActivity = config?.startActivity ?? defaultActivityConfig
  const endActivity = config?.endActivity ?? defaultActivityConfig

  // 비디오 유형이 선택되면 동영상 자료실 목록 로드
  useEffect(() => {
    const needsVideoList = (startActivity.types?.includes('video') && startActivity.enabled) ||
      (endActivity.types?.includes('video') && endActivity.enabled)

    if (needsVideoList && !videoListLoaded) {
      loadVideoList()
    }
  }, [startActivity.types, startActivity.enabled, endActivity.types, endActivity.enabled, videoListLoaded])

  const loadVideoList = async () => {
    setVideoListLoading(true)
    try {
      const response = await sharedMediaApi.getAll('video')
      if (response.data.success && response.data.data) {
        setVideoList(response.data.data)
      }
    } catch (error) {
      console.error('동영상 자료실 목록 로드 실패:', error)
    } finally {
      setVideoListLoading(false)
      setVideoListLoaded(true)
    }
  }

  const updateStartActivity = (updates: Partial<ScopedActivityConfig>) => {
    onChange({
      ...config,
      startActivity: { ...startActivity, ...updates },
      endActivity: config?.endActivity,
    })
  }

  const updateEndActivity = (updates: Partial<ScopedActivityConfig>) => {
    onChange({
      ...config,
      startActivity: config?.startActivity,
      endActivity: { ...endActivity, ...updates },
    })
  }

  const renderActivityEditor = (
    activity: ScopedActivityConfig,
    updateActivity: (updates: Partial<ScopedActivityConfig>) => void,
    type: 'start' | 'end'
  ) => {
    const isExpanded = expandedSection === type
    const Icon = type === 'start' ? Play : CheckCircle
    const title = type === 'start' ? '시작 활동' : '종료 활동'
    const description = type === 'start'
      ? 'Product 학습 시작 시 표시되는 활동입니다.'
      : 'Product 학습 완료 후 표시되는 활동입니다.'

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <button
          onClick={() => setExpandedSection(isExpanded ? null : type)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Icon className={`w-5 h-5 ${type === 'start' ? 'text-indigo-600' : 'text-green-600'}`} />
            <div className="text-left">
              <h4 className="font-medium text-gray-900">{title}</h4>
              <p className="text-xs text-gray-500">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={activity.enabled}
                onChange={(e) => updateActivity({ enabled: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">활성화</span>
            </label>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </button>

        {/* Content */}
        {isExpanded && activity.enabled && (
          <div className="p-4 space-y-4 border-t border-gray-200">
            {/* 시작활동: 비디오 → AIQ 퀴즈 순서 / 종료활동: AIQ 퀴즈 → 비디오 순서 */}
            {(type === 'start' ? ['video', 'quiz'] as const : ['quiz', 'video'] as const).map(activityType => (
              activityType === 'quiz' ? (
                <div key="quiz">
                  {/* AIQ 퀴즈 활동 */}
                  <div>
                    <label className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                      activity.types?.includes('quiz')
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="checkbox"
                        checked={activity.types?.includes('quiz') ?? false}
                        onChange={(e) => {
                          const currentTypes = activity.types ?? []
                          const newTypes = e.target.checked
                            ? [...currentTypes, 'quiz' as const]
                            : currentTypes.filter(t => t !== 'quiz')
                          updateActivity({
                            types: newTypes,
                            quizOptions: e.target.checked ? (activity.quizOptions ?? defaultQuizOptions) : undefined,
                          })
                        }}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <FileQuestion className="w-5 h-5 text-amber-600" />
                      <div>
                        <span className="font-medium text-gray-900">AIQ 퀴즈</span>
                        <p className="text-xs text-gray-500">Product 내 AIQ 퀴즈 랜덤 출제</p>
                      </div>
                    </label>
                  </div>

                  {/* Quiz Options */}
                  {activity.types?.includes('quiz') && (
                    <div className="space-y-3 pt-2 ml-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">문제 수</label>
                          <input
                            type="number"
                            min={1}
                            max={50}
                            value={activity.quizOptions?.questionCount ?? 20}
                            onChange={(e) => updateActivity({
                              quizOptions: {
                                ...activity.quizOptions,
                                questionCount: parseInt(e.target.value) || 20,
                                shuffleQuestions: activity.quizOptions?.shuffleQuestions ?? true,
                              }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">기본값: 20문제</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">문제 부족 시</label>
                          <select
                            value={activity.quizOptions?.questionShortageStrategy ?? 'available_only'}
                            onChange={(e) => updateActivity({
                              quizOptions: {
                                ...activity.quizOptions,
                                questionCount: activity.quizOptions?.questionCount ?? 20,
                                shuffleQuestions: activity.quizOptions?.shuffleQuestions ?? true,
                                questionShortageStrategy: e.target.value as 'available_only' | 'include_curriculum_incorrect',
                              }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
                          >
                            <option value="available_only">있는 만큼만</option>
                            <option value="include_curriculum_incorrect">전체 커리큘럼에서 오답 문제도 포함</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">통과 점수 (%)</label>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            placeholder="미설정"
                            value={activity.quizOptions?.passingScore ?? ''}
                            onChange={(e) => updateActivity({
                              quizOptions: {
                                ...activity.quizOptions,
                                questionCount: activity.quizOptions?.questionCount ?? 20,
                                shuffleQuestions: activity.quizOptions?.shuffleQuestions ?? true,
                                passingScore: e.target.value ? parseInt(e.target.value) : undefined,
                              }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">비워두면 점수와 무관하게 완료</p>
                        </div>
                      </div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={activity.quizOptions?.shuffleQuestions ?? true}
                          onChange={(e) => updateActivity({
                            quizOptions: {
                              ...activity.quizOptions,
                              questionCount: activity.quizOptions?.questionCount ?? 20,
                              shuffleQuestions: e.target.checked,
                            }
                          })}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">문제 순서 랜덤</span>
                      </label>
                    </div>
                  )}
                </div>
              ) : (
                <div key="video">
                  {/* 비디오 활동 */}
                  <div>
                    <label className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                      activity.types?.includes('video')
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="checkbox"
                        checked={activity.types?.includes('video') ?? false}
                        onChange={(e) => {
                          const currentTypes = activity.types ?? []
                          const newTypes = e.target.checked
                            ? [...currentTypes, 'video' as const]
                            : currentTypes.filter(t => t !== 'video')
                          updateActivity({
                            types: newTypes,
                            videoContentId: e.target.checked ? activity.videoContentId : undefined,
                          })
                        }}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <Video className="w-5 h-5 text-blue-600" />
                      <div>
                        <span className="font-medium text-gray-900">비디오</span>
                        <p className="text-xs text-gray-500">동영상 자료실에서 선택</p>
                      </div>
                    </label>
                  </div>

                  {/* Video Options - 동영상 자료실에서 선택 */}
                  {activity.types?.includes('video') && (
                    <div className="space-y-3 pt-2 ml-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          동영상 선택
                        </label>

                        {videoListLoading ? (
                          <div className="flex items-center gap-2 px-3 py-4 text-sm text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            동영상 자료실 로딩 중...
                          </div>
                        ) : videoList.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg bg-gray-50 text-center">
                            <Film className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p>동영상 자료실에 등록된 동영상이 없습니다.</p>
                            <p className="text-xs mt-1">
                              <a href="/library/videos" className="text-amber-600 hover:text-amber-700 underline">
                                동영상 자료실
                              </a>
                              에서 동영상을 먼저 업로드하세요.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <select
                              value={activity.videoContentId ?? ''}
                              onChange={(e) => {
                                const val = e.target.value
                                updateActivity({
                                  videoContentId: val ? parseInt(val) : undefined
                                })
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            >
                              <option value="">-- 동영상을 선택하세요 --</option>
                              {videoList.map(video => (
                                <option key={video.id} value={video.id}>
                                  {video.title}
                                  {video.durationSeconds ? ` (${Math.floor(video.durationSeconds / 60)}:${(video.durationSeconds % 60).toString().padStart(2, '0')})` : ''}
                                </option>
                              ))}
                            </select>

                            {activity.videoContentId && (
                              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                                <Video className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                <span className="text-blue-800">
                                  선택됨: {videoList.find(v => v.id === activity.videoContentId)?.title || `ID: ${activity.videoContentId}`}
                                </span>
                              </div>
                            )}

                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500">
                                동영상 자료실에서 업로드된 동영상 중 선택합니다.
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  setVideoListLoaded(false)
                                  loadVideoList()
                                }}
                                className="text-xs text-amber-600 hover:text-amber-700 underline"
                              >
                                새로고침
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            ))}
          </div>
        )}

        {/* Disabled state message */}
        {isExpanded && !activity.enabled && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500 text-center">
              활성화하면 {title} 설정이 표시됩니다.
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <h4 className="font-medium text-indigo-900 mb-1">Product 레벨 학습 활동</h4>
        <p className="text-sm text-indigo-700">
          Product 학습 시작 전/후에 표시되는 활동을 설정합니다.
          시작 활동은 첫 Lesson 학습 전에, 종료 활동은 모든 Lesson 완료 후에 표시됩니다.
        </p>
      </div>

      {onOverridableChange && (
        <label className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-100 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={overridable !== false}
            onChange={(e) => onOverridableChange(e.target.checked)}
            className="w-3.5 h-3.5 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
          />
          <span className={`text-sm ${overridable !== false ? 'text-purple-700' : 'text-gray-500'}`}>
            하위 레벨에서 변경 가능
          </span>
        </label>
      )}

      {renderActivityEditor(startActivity, updateStartActivity, 'start')}
      {renderActivityEditor(endActivity, updateEndActivity, 'end')}
    </div>
  )
}
